import { ReactNode } from "react";
import { useController, useFormContext } from "react-hook-form";

export type RenderLabelProps = {
  /** Field name */
  name: string;
  /** Whether the field has an error */
  error: boolean;
  /** Whether the field is required */
  required: boolean;
};

export type RenderInputProps = {
  /** Field name */
  name: string;
  /** Field id (same as name) */
  id: string;
  /** Current field value */
  value: any;
  /** Change handler */
  onChange: (value: any) => void;
  /** Blur handler */
  onBlur: () => void;
  /** Whether the field has an error */
  error: boolean;
  /** Whether the field is disabled */
  disabled: boolean;
  /** aria-describedby for error association */
  "aria-describedby"?: string;
  /** aria-labelledby for label association */
  "aria-labelledby"?: string;
  /** aria-required for required fields */
  "aria-required"?: boolean;
};

export type RenderErrorProps = {
  /** Error id for aria-describedby association */
  id: string;
  /** Error message */
  message?: string;
};

export type Props = {
  /** Field name - must match a key in the form schema */
  name: string;
  /** Whether the field is required (for visual indicator) */
  required?: boolean;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Additional CSS classes for wrapper */
  className?: string;
  /** Render function for the label */
  renderLabel?: (props: RenderLabelProps) => ReactNode;
  /** Render function for the input component */
  renderInput: (props: RenderInputProps) => ReactNode;
  /** Render function for the error message */
  renderError?: (props: RenderErrorProps) => ReactNode;
  /** Render function for the wrapper */
  renderWrapper?: (props: { children: ReactNode }) => ReactNode;
};

/**
 * Base form field component that provides RHF binding via render props.
 * No styling - use render props to customize label, input, and error display.
 *
 * @example
 * <FormFieldBase
 *   name="email"
 *   required
 *   renderLabel={({ error, required }) => (
 *     <label>Email {required && '*'}</label>
 *   )}
 *   renderInput={({ value, onChange, onBlur, error, disabled }) => (
 *     <input value={value} onChange={e => onChange(e.target.value)} />
 *   )}
 *   renderError={({ message }) => message && <span>{message}</span>}
 * />
 */
export function FormFieldBase({
  name,
  required = false,
  disabled = false,
  className,
  renderLabel,
  renderInput,
  renderError,
  renderWrapper,
}: Props) {
  const { control, formState } = useFormContext();
  const { isSubmitting } = formState;

  const {
    field: { value, onChange, onBlur },
    fieldState: { error, isTouched },
  } = useController({
    name,
    control,
  });

  const { isSubmitted } = formState;
  const isDisabled = disabled || isSubmitting;
  const hasError = !!error && (isTouched || isSubmitted);

  const labelProps: RenderLabelProps = {
    name,
    error: hasError,
    required,
  };

  const inputProps: RenderInputProps = {
    name,
    id: name,
    value,
    onChange,
    onBlur,
    error: hasError,
    disabled: isDisabled,
    "aria-describedby": hasError ? `${name}-error` : undefined,
    "aria-labelledby": renderLabel ? `${name}-label` : undefined,
    "aria-required": required || undefined,
  };

  const errorProps: RenderErrorProps = {
    id: `${name}-error`,
    message: hasError ? error?.message : undefined,
  };

  const content = (
    <>
      {renderLabel?.(labelProps)}
      <div className="flex flex-col gap-2 w-full">
        {renderInput(inputProps)}
        {renderError?.(errorProps)}
      </div>
    </>
  );

  if (renderWrapper) {
    return renderWrapper({ children: content });
  }

  return <div className={className}>{content}</div>;
}

FormFieldBase.displayName = "FormFieldBase";

export default FormFieldBase;
